import {
  createContext,
  createElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { workspacesApi, Workspace } from '../api/workspaces';
import { useAuth } from './useAuth';

interface WorkspaceContextValue {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  switchWorkspace: (workspace: Workspace) => void;
  loading: boolean;
  error: string;
  refetch: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchWorkspaces = useCallback(async () => {
    if (!isAuthenticated) {
      setWorkspaces([]);
      setCurrentWorkspace(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await workspacesApi.list();
      const items = Array.isArray(res.data) ? res.data : [];
      setWorkspaces(items);
      const storedId = localStorage.getItem('saik_workspace_id');
      const selected = items.find((workspace) => workspace.id === storedId) || items[0] || null;
      setCurrentWorkspace(selected);
      if (selected) {
        localStorage.setItem('saik_workspace_id', selected.id);
      } else {
        localStorage.removeItem('saik_workspace_id');
      }
    } catch (err: any) {
      setWorkspaces([]);
      setCurrentWorkspace(null);
      setError(err?.response?.data?.detail || 'Unable to load workspaces');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  const switchWorkspace = useCallback((ws: Workspace) => {
    setCurrentWorkspace(ws);
    localStorage.setItem('saik_workspace_id', ws.id);
  }, []);

  return createElement(
    WorkspaceContext.Provider,
    {
      value: {
        workspaces,
        currentWorkspace,
        switchWorkspace,
        loading,
        error,
        refetch: fetchWorkspaces,
      },
    },
    children,
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
}
